import React, { useState } from 'react';
import DropdownCustom from '@src/new-components/Dropdown';
import { COLOR } from '@src/styles/color';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CommonButton from '@src/new-components/Button';
import apis from '@src/apis';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import BadgeAvatar from '@src/components/BadgeAvatar';
import actions from '@src/redux/actions';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useDispatch, useSelector } from 'react-redux';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import Typography from '@src/new-components/Typography';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Divider, IconButton } from '@mui/material';
import {
  StyleTTSPreset,
  StyleTTSPresetActions,
  StyleTTSPresetContent,
  StyleTTSPresetItem,
} from './index.style';

const MAX_TTS_PRESET = 20;

const TTSPreset = ({
  ttsPresetData,
  refetchListTTSPreset,
  handleOpenVoicePresetDialog,
  handleChangeVoice,
  openVoicePresetDialog,
  openTTSPreset,
  handleOpenTTSPreset,
  handleCloseTTSPreset,
  loadingGetListTTSPreset,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [presetDelete, setPresetDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { synthesisRequest, ttsPreset } = useSelector(
    (state) => state.synthesisRequest,
  );

  const [openEdit, setOpenEdit] = useState();
  const listTTSPreset = ttsPresetData?.ttsPresets || [];

  const handleEdit = (data) => {
    handleOpenVoicePresetDialog(data);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    setPresetDelete({
      id,
      name: ttsPresetData?.ttsPresets.find((item) => item.id === id)?.name,
    });
  };

  const handleCreateNew = (e) => {
    e.stopPropagation();
    handleOpenVoicePresetDialog(null);
  };

  const handleSelectPreset = (preset) => {
    const dataSynthesis = ttsPresetData?.ttsPresets.find(
      (item) => item.id === preset,
    );

    dispatch(
      actions.synthesisRequest.updateSynthesisConfig({
        ...synthesisRequest,
        ...(dataSynthesis.audioType && { audioType: dataSynthesis.audioType }),
        backgroundMusic: dataSynthesis.backgroundMusic,
        ...(dataSynthesis.speed && { speed: dataSynthesis.speed }),
        ...(dataSynthesis.volume && { volume: dataSynthesis.volume }),
        ...(dataSynthesis.clientPause && {
          clientPause: dataSynthesis.clientPause,
        }),
        ...(dataSynthesis.voice && { voice: dataSynthesis.voice }),
      }),
    );
    dispatch(actions.synthesisRequest.updateCurrentTTSPreset(dataSynthesis));
    handleChangeVoice(dataSynthesis.voice);
  };

  const renderActions = (id) =>
    openEdit === id && (
      <StyleTTSPresetActions>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(
              ttsPresetData?.ttsPresets?.find((item) => item.id === id),
            );
          }}
        >
          <EditRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
        </IconButton>
        <IconButton onClick={(e) => handleDelete(e, id)}>
          <DeleteRoundedIcon sx={{ fontSize: 24, color: COLOR.textRejected }} />
        </IconButton>
      </StyleTTSPresetActions>
    );

  // Delete preset
  const handleDeletePreset = async () => {
    try {
      setLoadingDelete(true);
      const response = await apis.ttsPreset.deleteTTSPreset(presetDelete?.id);
      if (response.status) {
        if (ttsPreset?.id === presetDelete?.id) {
          dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
        }
        refetchListTTSPreset();
        setPresetDelete(null);
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: 'deleteTtsPresetSuccess',
          }),
        );
      }
      setLoadingDelete(false);
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'deleteTtsPresetFailed',
        }),
      );
    }
  };

  const renderActionsDeletePreset = () => (
    <>
      <CommonButton
        customStyle={{
          padding: '8px 24px',
          borderRadius: '8px',
        }}
        variant="outlineSmall"
        textProps={{
          variant: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
        onClick={() => setPresetDelete(null)}
      >
        {t('cancel')}
      </CommonButton>
      <CommonButton
        customStyle={{
          padding: '8px 24px',
          backgroundColor: COLOR.error,
          borderRadius: '8px',
          '&:disabled': {
            backgroundColor: COLOR.error,
            opacity: 0.5,
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          },
        }}
        textProps={{
          variant: 'body-14-semibold',
          color: COLOR.white,
        }}
        color="error"
        variant="contained"
        onClick={handleDeletePreset}
        disabled={loadingDelete}
        startIcon={
          loadingDelete ? (
            <CircularProgress size={20} sx={{ color: COLOR.white }} />
          ) : null
        }
      >
        {t('delete')}
      </CommonButton>
    </>
  );

  return (
    <div className="group">
      <Divider orientation="vertical" flexItem className="divider" />
      <DropdownCustom
        open={openTTSPreset}
        onOpen={handleOpenTTSPreset}
        onClose={handleCloseTTSPreset}
        loading={loadingGetListTTSPreset}
        isSelectingOption={ttsPreset?.name}
        widthContentEmpty={!listTTSPreset?.length ? '309px' : null}
        contentEmpty={
          <Typography
            component="p"
            variant="body-14-regular"
            color={COLOR.descriptionUpload}
          >
            {t('noteListTTSPresetEmpty')}
          </Typography>
        }
        options={listTTSPreset?.map((item) => ({
          label: (
            <StyleTTSPreset
              key={item?.id}
              onMouseEnter={() => setOpenEdit(item?.id)}
              onMouseLeave={() => setOpenEdit(null)}
            >
              <StyleTTSPresetItem>
                <div style={{ marginRight: '8px' }}>
                  <BadgeAvatar
                    width={24}
                    img={item?.voice?.roundImage}
                    smallImg={
                      item?.voice &&
                      item?.voice?.language &&
                      item?.voice?.language.roundImage
                    }
                    type="image"
                  />
                </div>
                <Typography
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: openEdit === item?.id && '104px',
                  }}
                  variant="body-14-medium"
                  color={COLOR.darkGray}
                >
                  {item?.name}
                </Typography>
              </StyleTTSPresetItem>
              {renderActions(item.id)}
            </StyleTTSPreset>
          ),
          value: item?.id,
        }))}
        value={ttsPreset?.id}
        onChange={handleSelectPreset}
        variant="config-voice"
        startIcon={
          <SettingsSuggestRoundedIcon
            sx={{ fontSize: 24, color: COLOR.navyBlue }}
          />
        }
        content={
          <StyleTTSPresetContent>
            <CommonButton
              onClick={handleCreateNew}
              startIcon={
                <AddRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
              }
              variant="outlineSmall"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                height: '40px',
                width: '115px',
              }}
              textProps={{
                color: COLOR.darkBlue,
                variantText: 'body-14-medium',
              }}
              disabled={listTTSPreset?.length === MAX_TTS_PRESET}
            >
              {t('createNewV2')}
            </CommonButton>
            <Typography variant="body-14-medium" color={COLOR.darkGray}>
              {ttsPresetData?.total || 0}/{MAX_TTS_PRESET}
            </Typography>
          </StyleTTSPresetContent>
        }
        tooltipContent={
          <>
            <Typography
              component="p"
              variant="body-14-regular"
              color={COLOR.whiteV2.default}
            >
              {t('voicePresetV2')}
            </Typography>
            <Typography
              component="p"
              variant="body-14-regular"
              color={COLOR.whiteV2.default}
            >
              {ttsPreset?.name}
            </Typography>
          </>
        }
        stopClickOutside={presetDelete || openVoicePresetDialog}
      />

      <CommonDialog
        width="512px"
        title={t('deleteVoicePreset')}
        fullWidth
        contentComponent={
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('confirmDeleteVoicePreset', {
                name: presetDelete?.name,
              }),
            }}
          />
        }
        open={presetDelete}
        onClose={() => setPresetDelete(null)}
        actionComponent={renderActionsDeletePreset()}
      />
    </div>
  );
};

export default TTSPreset;
